// @flow

import * as React from 'react';
import classnames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { localize, unescaped } from '@ta/common.i18n';
import { Opener } from '@ta/overlays.managers';
import { Modal } from '@ta/overlays.modal';
import { TitleBar } from '@ta/modal.headers';
import * as Import from '@ta/platform.import';

import styles from './ContentCollectionUtility.less';

const LinkPostUploader = Import.component.onUsage(
  () => import('@ta/social.linkpost-upload'),
  'LinkPostUploader',
);
const MediaUploaderModal = Import.component.onUsage(
  () => import('@ta/media.upload-exports'),
  'MediaUploaderModal',
);

const linkToastMessage = (url: string, pageName: string) =>
  pageName === 'Profile'
    ? unescaped(localize('upload_link_posted_copy_modal_profile')) // Your link was posted!
    : unescaped(localize('upload_link_posted_copy_modal_2', { '0': url, '1': styles.toastLink })); // Your link was posted! <a href="{0}" class="{1}">View it on your profile now.</a>
const photoToastMessage = (url: string, pageName: string) =>
  pageName === 'Profile'
    ? unescaped(localize('upload_photo_posted_copy_modal_profile')) // Your photo was posted!
    : unescaped(localize('upload_photo_posted_copy_modal_2', { '0': url, '1': styles.toastLink })); // Your photo was posted! <a href="{0}" class="{1}">View it on your profile now.</a>

/**
 This helper method is used to get pid based on servlet , then it is used for Content Upload Tracking.
 FYI: https://docs.tamg.io/display/SEO/Content+collection+CTA+task+list
 */
export const getPid = (pageName: string) => {
  // Different servlet has different pid.
  if (pageName === 'Hotel_Review') {
    return 40537;
  } else if (pageName === 'Restaurant_Review') {
    return 40513;
  } else if (pageName === 'Attraction_Review') {
    return 40536;
  } else if (pageName === 'Hotels') {
    return 40538;
  } else if (pageName === 'Restaurants') {
    return 40512;
  } else if (pageName === 'Attractions') {
    return 40535;
  } else if (pageName === 'Home') {
    return 40488;
  } else if (pageName === 'Profile') {
    return 40489;
  } else if (pageName === 'SmartDeals') {
    return 40539;
  } else if (pageName === 'ShowTopic') {
    return 40540;
  } else if (pageName === 'Tourism') {
    return 40541;
  } else if (pageName.startsWith('ShowUserReviews')) {
    return 40542;
  } else if (pageName === 'ShowForum' || pageName === 'ForumHome') {
    return 40544;
  } else if (pageName === 'Airline_Review') {
    return 39771;
  } else if (pageName === 'Airlines') {
    return 39772;
  } else if (pageName === 'Cruise_Review') {
    return 40514;
  }
  // All the other servlet will be using this pid.
  return 40543;
};

/**
 * Used for get the text based on slice. We either show 'Post' or empty string
 */
export const getTitle = () => {
  if (featureIsEnabled('add_cta_to_global_nav')) {
    return localize('global_nav_content_cta_post'); // This translation string represent 'Post' in English;
  }
  return '';
};

/**
 * Used for get the Symbol based on slice. We either show a filled one or a unfilled one.
 */
export const getIconName = () => {
  if (featureIsEnabled('add_cta_to_global_nav')) {
    return 'plus-circle';
  }
  return '';
};

/**
 * This method is used to show the corresponding media uploader modal after user clicks the 'Post Photos', 'Post Videos' or 'Post Links' in global nav bar.
 */
export const ShowOverlayUploaderModal = (
  modalname: string,
  pid: number,
  currentLocale: string,
  profileUrl: ?string,
  locationId: number,
  ifShowLinkUploader: (value: boolean) => void,
  pageName: string,
) => {
  if (profileUrl) {
    const linkModalName = 'link-uploader-nav';
    const linkToastOpenerName = 'link-toast-overlay';
    const videoModalName = 'video-uploader-nav';
    const videoToastOpenerName = 'video-toast-overlay';
    const photoModalName = 'photo-uploader-nav';
    const photoToastOpenerName = 'photo-toast-overlay';

    if (modalname === linkModalName) {
      return (
        <React.Fragment>
          <Opener name={linkToastOpenerName}>
            {open => (
              <LinkPostUploader
                modalName={linkModalName}
                onSubmit={open}
                displaySuccessModal={false}
                locationId={locationId}
              />
            )}
          </Opener>
          <Modal
            name={linkToastOpenerName}
            header={
              <TitleBar iconName="link" displayType="LEFT_ALIGN_GRAY">
                <span className={styles.toastTitle}>{localize('link_uploader_header')}</span>
              </TitleBar>
            }
          >
            <div className={classnames(styles.toastMessge)}>
              {linkToastMessage(profileUrl, pageName)}
            </div>
          </Modal>
        </React.Fragment>
      );
    } else if (modalname === videoModalName) {
      return (
        <React.Fragment>
          <Opener name={videoToastOpenerName}>
            {open => (
              <MediaUploaderModal
                mediaType="video"
                modalName={videoModalName}
                linkUploaderModalName={linkModalName}
                key="video"
                pid={pid}
                onSubmit={open}
                locationId={locationId}
                showLinkUploaderOfGNavVideoModalLink={ifShowLinkUploader}
                linkUploaderModalNameOfGNavVideoModalLink="link-uploader-from-video-uploader"
              />
            )}
          </Opener>
          <Modal
            name={videoToastOpenerName}
            header={
              <TitleBar iconName="video-fill" displayType="LEFT_ALIGN_GRAY">
                <span className={styles.toastTitle}>{localize('post_a_video_modal_header')}</span>
              </TitleBar>
            }
          >
            <div className={classnames(styles.toastMessge)}>
              {localize('upload_video_processing_copy_modal')}
            </div>
          </Modal>
        </React.Fragment>
      );
    } else if (modalname === photoModalName) {
      return (
        <React.Fragment>
          <Opener name={photoToastOpenerName}>
            {open => (
              <MediaUploaderModal
                mediaType="photo"
                modalName={photoModalName}
                linkUploaderModalName={linkModalName}
                key="photo"
                pid={pid}
                onSubmit={open}
                locationId={locationId}
              />
            )}
          </Opener>
          <Modal
            name={photoToastOpenerName}
            header={
              <TitleBar iconName="camera-fill" displayType="LEFT_ALIGN_GRAY">
                <span className={styles.toastTitle}>{localize('post_photos_form_header')}</span>
              </TitleBar>
            }
          >
            <div className={classnames(styles.toastMessge)}>
              {/* $FlowFixMe92 */}
              {photoToastMessage(profileUrl, pageName)}
            </div>
          </Modal>
        </React.Fragment>
      );
    }
  }
  return null;
};

/**
 * Render the LinkUploader from the link of video uploader
 */
export const showLinkUploaderFromVideoUploader = (
  shouldShowLinkUploader: boolean,
  currentLocale: string,
  profileUrl: ?string,
  locationId: number,
  pageName: string,
) => {
  const linkToastOpenName = 'link-toast-overlay';
  const linkModalName = 'link-uploader-from-video-uploader';

  if (shouldShowLinkUploader && profileUrl) {
    return (
      <div>
        <Opener name={linkToastOpenName}>
          {open => (
            <LinkPostUploader modalName={linkModalName} onSubmit={open} locationId={locationId} />
          )}
        </Opener>
        <Modal
          name={linkToastOpenName}
          header={
            <TitleBar iconName="link" displayType="LEFT_ALIGN_GRAY">
              <span style={{ fontSize: '16px', lineHeight: '18px' }}>
                {localize('link_uploader_header')}
              </span>
            </TitleBar>
          }
        >
          <div className={classnames(styles.toastMessge)}>
            {/* $FlowFixMe92 */}
            {linkToastMessage(profileUrl, pageName)}
          </div>
        </Modal>
      </div>
    );
  }
  return null;
};
